<template>
    <header class="header-shell fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out">
      <div class="mx-auto px-6">
        <div class="flex items-center justify-between h-16">
          <div class="flex items-center">
            <!-- Logo部分 -->
            <AppLogo class="w-auto h-8" :variant="'light'" @logo-click="trackLogoNavigation" />
            <!-- 导航链接 -->
            <div class="hidden md:flex space-x-1 ml-14">
              <button class="nav-button relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-md" @click="navigateHome">{{ t('header.home') }}</button>
              <button class="nav-button relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-md" @click="navigateUpload">{{ t('header.upload') }}</button>
              <button class="nav-button relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-md" @click="navigateHistory">{{ t('header.history') }}</button>
            </div>
          </div>

          <!-- 用户菜单（包含登录按钮） -->
          <UserMenu />
        </div>
      </div>
    </header>
  </template>
  
<script setup>
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import UserMenu from './UserMenu.vue';
import AppLogo from './AppLogo.vue';
import { useUserStore } from '@/core/store/useUserStore';
import { useNavigationAnalytics } from '@/core/analytics/hooks/useNavigationAnalytics';

const router = useRouter();
const { t } = useI18n();
const userStore = useUserStore();
const navigationAnalytics = useNavigationAnalytics();

const navigateUpload = () => {
  const currentLang = router.currentRoute.value.params.lang;
  navigationAnalytics.trackAppNavigationClicked({
    sourcePosition: 'header_nav',
    elementName: 'upload',
    elementType: 'button',
    targetRouteName: 'upload',
    targetRouteType: 'internal',
    method: 'user_click',
    lang: currentLang,
    isLoggedIn: userStore.isLoggedIn
  });
  router.push(`/${currentLang}/upload`);
};

// 首页入口：统一跳 LandingPage（/landing），不受登录态限制
const navigateHome = () => {
  const currentLang = router.currentRoute.value.params.lang;
  navigationAnalytics.trackAppNavigationClicked({
    sourcePosition: 'header_nav',
    elementName: 'home',
    elementType: 'button',
    // targetRouteName 须与下方 router.push 的目标路由 name 一致，
    // 否则导航埋点 target_route_name 无法对应真实路由（router 中无 name='home'）。
    targetRouteName: 'LandingPage',
    targetRouteType: 'internal',
    method: 'user_click',
    lang: currentLang,
    isLoggedIn: userStore.isLoggedIn
  });
  router.push({ name: 'LandingPage', params: { lang: currentLang } });
};

const navigateHistory = () => {
  const currentLang = router.currentRoute.value.params.lang;
  navigationAnalytics.trackAppNavigationClicked({
    sourcePosition: 'header_nav',
    elementName: 'history',
    elementType: 'button',
    targetRouteName: 'history',
    targetRouteType: 'internal',
    method: 'user_click',
    lang: currentLang,
    isLoggedIn: userStore.isLoggedIn
  });
  router.push(`/${currentLang}/history`);
};

// Logo 点击：与相邻导航按钮同构，走 app_navigation_clicked + element_name='logo'。
// targetRouteName 跟随 AppLogo 内部真实跳转目标 /:lang/home（router 中 name='Landing'），
// 不要写成 'LandingPage'（/landing）—— 那是下方首页按钮的目标，与 logo 不一致。
const trackLogoNavigation = () => {
  const currentLang = router.currentRoute.value.params.lang;
  navigationAnalytics.trackAppNavigationClicked({
    sourcePosition: 'header_nav',
    elementName: 'logo',
    elementType: 'link',
    targetRouteName: 'Landing',
    targetRouteType: 'internal',
    method: 'user_click',
    lang: currentLang,
    isLoggedIn: userStore.isLoggedIn
  });
};
</script>

<style scoped>
.nav-button {
  position: relative;
  overflow: hidden;
  color: var(--text-secondary);
}

.nav-button:hover {
  color: var(--text-primary);
  background-color: var(--button-secondary-hover);
}

.nav-button::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, var(--theme-gradient-start), var(--theme-gradient-end));
  transform: scaleX(0);
  transition: transform 0.3s ease;
  transform-origin: right;
}

.nav-button:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}

/* 平滑过渡效果 */
header {
  transition: all 0.3s ease-in-out;
}

.header-shell {
  background-color: var(--app-page-bg);
  box-shadow: 0 1px 4px color-mix(in srgb, var(--shadow-color) 45%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--border-default) 70%, transparent);
}

/* 滚动时的样式 */
:global(body.scrolled) .header-shell {
  background-color: var(--app-page-bg);
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--shadow-color) 50%, transparent);
}
</style>
